// packages/ui-kit/src/primitives/Input.tsx
// C3F v18 — reference: design/system/components/forms/Input.{jsx,prompt.md}
import { type InputHTMLAttributes, type ReactNode, forwardRef } from "react";

type Size = "sm" | "md" | "lg";

interface Props extends Omit<InputHTMLAttributes<HTMLInputElement>, "size" | "prefix" | "suffix"> {
  size?: Size;
  prefix?: ReactNode;
  suffix?: ReactNode;
  error?: boolean;
}

// DS heights: sm 38 · md 44 · lg 52. On touch viewports sm keeps the 44px
// floor and drops to the dense 38px at the `sm:` breakpoint.
const boxCls: Record<Size, string> = {
  sm: "min-h-touch sm:min-h-[38px] px-3",
  md: "min-h-touch px-3.5",
  lg: "min-h-[52px] px-3.5",
};

// >=16px body on phones prevents iOS focus-zoom; DS desktop body is 14px.
const fontCls: Record<Size, string> = {
  sm: "text-base sm:text-sm",
  md: "text-base sm:text-sm",
  lg: "text-base",
};

// Fields use --border-control (WCAG 1.4.11), never --border-default, and hover
// NEVER darkens the border — state changes are border-focus + ring only.
const fieldBase =
  "w-full rounded-md border bg-elevated font-sans text-fg-default placeholder:text-fg-muted outline-none transition-[border-color,box-shadow] duration-fast disabled:cursor-not-allowed disabled:opacity-45";

const borderFor = (error: boolean, within: boolean) =>
  error
    ? "border-danger-strong"
    : within
      ? "border-border-control focus-within:border-border-focus"
      : "border-border-control focus-visible:border-border-focus";

export const Input = forwardRef<HTMLInputElement, Props>(function Input(
  { size = "md", prefix, suffix, error = false, className = "", ...rest },
  ref,
) {
  if (!prefix && !suffix) {
    return (
      <input
        ref={ref}
        className={`block ${fieldBase} ${borderFor(error, false)} focus-visible:shadow-focus ${boxCls[size]} ${fontCls[size]} ${className}`}
        {...rest}
      />
    );
  }
  return (
    <span
      className={`flex items-center gap-2 ${fieldBase} ${borderFor(error, true)} focus-within:shadow-focus ${boxCls[size]} ${fontCls[size]} ${className}`}
    >
      {prefix && <span className="flex shrink-0 text-fg-muted">{prefix}</span>}
      <input
        ref={ref}
        className={`min-w-0 flex-1 self-stretch border-0 bg-transparent font-sans text-fg-default outline-none placeholder:text-fg-muted ${fontCls[size]}`}
        {...rest}
      />
      {suffix && <span className="flex shrink-0 text-fg-muted">{suffix}</span>}
    </span>
  );
});
